import { useEffect, useMemo, useState } from 'react';
import { usePortfolio } from './usePortfolio';
import { V2_METRICS, metricCoverageTotals, reportingCurrency, campaignPeriodDeltas } from './v2Metrics';
import PerformanceV2Summary from './PerformanceV2Summary';
import CampaignFilter from '../CampaignFilter';
import TrendChart from './TrendChart';
import {
  CPA_MIN_CONVERSIONS, CPA_MIN_WORSENING, MOVERS_MIN_DELTA, NEEDS, applyWindow,
  campaignRows, cpaDeterioration, daysToFlightEnd, fmt, health, isUnavailable,
  metricsWindow, money, scorecard, signed, topMovers, topSpenders, totals,
  unavailable,
} from './portfolioData';
// The SAME preset vocabulary the Campaign Grid's picker uses, so "Last 30 days"
// cannot come to mean two different spans on two pages. What differs is what a
// pick DOES — see the picker's own comment below.
import {
  GRID_TIME_PRESETS, describeTimeWindow, loadStoredTimeFilter, resolveTimeWindow,
  storeTimeFilter,
} from '../../../utils/campaignGridTimeFilter';
import './portfolio.css';
import PortfolioOverviewDrilldown, { overviewMatches } from './PortfolioOverviewDrilldown';
import { googlePortfolioContext } from '../../OptimizationWorkspace/GooglePortfolioTable';

/**
 * Summary — the account-level dashboard, ported from the design project's
 * performance-portfolio.html.
 *
 * WHAT THIS VIEW IS FOR, relative to the tabs beside it: Diagnostics, Budget &
 * pacing and Optimization each render one agent's findings. This renders the
 * ACCOUNT — delivery across every campaign, with the agents' findings folded in
 * as a health read rather than as the subject. It is the design's "Account read"
 * and sits first because it is where someone lands before drilling in.
 *
 * READ-ONLY, like every other tab here. Nothing on this view dispatches a
 * metrics pull, and it carries no refresh control of its own: a pull costs money
 * and belongs to the Campaign Grid's own explicit control, and re-reading the
 * same cache from a button here only looked like it did something.
 *
 * Blocks that remain but depend on data nothing in this system persists carry
 * an explicit "Not wired" statement. They never fill missing inputs with
 * plausible numbers. See portfolioData.js.
 */

/** The metrics the trend chart can switch between, once it has a series. */
const TREND_METRICS = [
  { key: 'cost', label: 'Cost', format: 'currency' },
  { key: 'conversions', label: 'Conversions', format: 'int' },
  { key: 'cpa', label: 'Cost / conv.', format: 'currency2' },
  // rate:true — averaged across days in the legend, never summed. A summed CTR
  // is a meaningless number that still renders.
  { key: 'ctr', label: 'CTR', format: 'pct', rate: true },
];

// The series is summed across every advertiser the account pulled, in each
// advertiser's OWN currency, and nothing converts. So on a mixed-currency
// account the money metrics plot a line that is not a quantity of anything —
// the same defect as the scorecard's Cost tile, in a form that looks even more
// authoritative because it has a shape. Offer only what is currency-free.
const CURRENCY_FREE_TREND_METRICS = TREND_METRICS.filter((m) => !String(m.format).startsWith('currency'));

/**
 * The campaign table's column groups.
 *
 * The design keeps Google's own taxonomy — Performance · Conversions ·
 * Competitive · Quality · Attributes — on the stated grounds that "that taxonomy
 * is what people are trained on". Two of the five have no stored source, and
 * carry `needs` instead of columns rather than being quietly dropped: a missing
 * tab would misrepresent the account as having no competitive data, when what is
 * true is that we do not pull any.
 */
const COLUMN_GROUPS = [
  {
    key: 'performance',
    label: 'Performance',
    cols: [
      { key: 'cost', label: 'Cost', format: 'currency', num: true },
      { key: 'impressions', label: 'Impr.', format: 'int', num: true },
      { key: 'clicks', label: 'Clicks', format: 'int', num: true },
      { key: 'ctr', label: 'CTR', format: 'pct', num: true },
      { key: 'cpc', label: 'Avg CPC', format: 'currency2', num: true },
    ],
  },
  {
    key: 'conversions',
    label: 'Conversions',
    cols: [
      { key: 'conversions', label: 'Conv.', format: 'int', num: true },
      { key: 'cpa', label: 'Cost / conv.', format: 'currency2', num: true },
    ],
    note: 'Conversion VALUE is not stored — only a count — so value/conv. and ROAS are absent rather than derived from a count.',
  },
  {
    key: 'competitive',
    label: 'Competitive',
    // Sourced from optimizer RUNS, not from the metrics pull — which is why the
    // caption's no-delta caveat does not apply to it and must not be printed
    // over it. See the caption.
    fromRuns: true,
    cols: [
      { key: 'competitive', label: 'Auction read', render: 'competitive' },
    ],
    note: 'Read out of the growth run, not out of a metrics pull — no impression-share '
      + 'figure is stored (' + NEEDS.impressionShare + '). A campaign whose analysis said '
      + 'nothing about the auction is left empty rather than shown a zero.',
  },
  {
    key: 'quality',
    label: 'Quality',
    fromRuns: true,
    cols: [
      { key: 'quality', label: 'Delivery quality', render: 'quality' },
    ],
    note: 'NOT Google\u2019s Quality Score, which is per keyword, 1\u201310, and which no pull '
      + 'stores. This is how much of what the campaign is built out of can actually serve, '
      + 'scored 0\u2013100 from its own latest diagnostic run. No diagnostic run, no score.',
  },
  {
    key: 'attributes',
    label: 'Attributes',
    cols: [
      { key: 'type', label: 'Type' },
      { key: 'status', label: 'Status' },
      { key: 'flightEnd', label: 'Flight ends' },
    ],
  },
];

const V2_CONVERSION_KEYS = ['conversions', 'cpa', 'conversionsValue', 'roas', 'valuePerConversion', 'allConversions', 'allConversionsValue', 'viewThroughConversions', 'conversionRate'];
const V2_COLUMN_GROUPS = COLUMN_GROUPS.map((g) => g.key === 'conversions' ? {
  ...g,
  cols: V2_CONVERSION_KEYS.map((key) => ({ ...V2_METRICS.find((m) => m.key === key), num: true })),
  note: 'Extended Google metrics come from reported daily data for the selected range. A dash means the metric was not reported or its denominator is zero.',
} : g);

/**
 * The ranked list whose input we still do not hold.
 *
 * The three that left: CPA deterioration and both Top movers lists. The prior
 * period all of them needed turned out to be derivable from the daily rows in
 * the last pull's artifact, which is where the trend chart's comparison overlay
 * already comes from. Top movers was held back from that first pass on the
 * grounds that "Δ conv." wants its own volume gate — it does, and the answer is
 * that ranking by the absolute change IS the gate. See topMovers.
 *
 * Capped and converting stays: impression share is not stored anywhere, by
 * anything, so no arrangement of the rows we do have produces it.
 */
const UNWIRED_LISTS = [
  {
    key: 'capped', icon: 'lock_open', color: '#00785e', title: 'Capped and converting', meta: 'headroom', needs: NEEDS.impressionShare,
  },
];

/**
 * The competitive cell.
 *
 * EMPTY IS THE COMMON CASE and is rendered as the same em-dash every other
 * absent value uses. Most campaigns' runs never mention the auction, and a
 * badge saying "no competitive data" on forty rows would turn an absence into
 * forty pieces of noise.
 *
 * When there IS a read, the percentage leads only if one was actually cited.
 * A signal with no figure behind it shows its label alone rather than a number
 * borrowed from somewhere else in the sentence — see citedPct in portfolioData.
 */
function CompetitiveCell({ read }) {
  if (!read) return <span className="tnone">—</span>;
  return (
    <span
      className={`cq${read.rankConstrained ? ' hot' : ''}`}
      // The citation behind the cell. It is quoting a run, and a reader who
      // wants to check it should not have to open another tab to find out which
      // sentence it came from.
      title={read.evidence.join('\n\n') || undefined}
    >
      {read.rankConstrained ? (
        <span aria-hidden="true" className="material-icons">gavel</span>
      ) : null}
      {read.pct !== null ? <b>{`${read.pct}%`}</b> : null}
      <span className="cql">{read.label}</span>
      {read.n > 1 ? <span className="cqn">{`\u00d7${read.n}`}</span> : null}
    </span>
  );
}

/**
 * The quality cell: the score, its band, and the run's own verdict beside it.
 *
 * The deductions go in the title rather than the cell. A score nobody can
 * attribute is a number to argue with; the arithmetic is what makes it a
 * finding. It is not inlined because forty rows of it is a wall, not a table.
 */
function QualityCell({ q }) {
  if (!q) return <span className="tnone">—</span>;
  const lines = q.deductions.length
    ? q.deductions.map((d) => `\u2212${d.points}  ${d.label}`).join('\n')
    : 'Nothing deducted \u2014 the diagnostic found no blocked delivery, no measurement '
      + 'problem and no high-severity blockers.';
  return (
    <span
      className={`qs ${q.band}`}
      title={`${lines}\n\nFrom the diagnostic run${q.at ? ` of ${String(q.at).slice(0, 10)}` : ''}.`}
    >
      <b>{q.score}</b>
      <span className="qsl">{q.bandLabel}</span>
      {/* The run's own HEALTHY/DEGRADED/STALLED, shown only when it does not
          simply echo the band — a "Good 88" next to "HEALTHY" is one fact
          printed twice, but a "Good 82" next to "DEGRADED" is worth seeing. */}
      {q.verdict && q.verdict.toUpperCase() !== 'HEALTHY' ? (
        <span className="qsv">{q.verdict.toLowerCase()}</span>
      ) : null}
    </span>
  );
}

function NotWired({ children }) {
  return (
    <div className="nw">
      <div className="hd">
        <span aria-hidden="true" className="material-icons">pending</span>
        Not wired
      </div>
      {children}
    </div>
  );
}

/** One scorecard tile. */
function Tile({ tile, currency }) {
  const dead = isUnavailable(tile.value);
  return (
    <div className="kc">
      <div className="l">{tile.label}</div>
      <div className={`v${dead ? ' none' : ''}`}>
        {dead ? '—' : fmt(tile.value, tile.format, currency)}
      </div>
      {dead
        ? <div className="nd">{tile.value.reason}</div>
        : <div className="nd">no prior period to compare</div>}
    </div>
  );
}

function ListFrame({
  icon, color, title, meta, children, foot, onViewAll,
}) {
  return (
    <div className="lst">
      <div className="lh">
        <span aria-hidden="true" className="material-icons" style={{ color }}>{icon}</span>
        <span className="t">{title}</span>
        <span className="g" />
        {meta ? <span className="m">{meta}</span> : null}
      </div>
      {children}
      {foot ? <div className="lf">{foot}</div> : null}
      {onViewAll && <div className="ow-list-action"><button type="button" onClick={onViewAll} aria-label={`View all campaigns: ${title}`}>View all campaigns →</button></div>}
    </div>
  );
}

/**
 * The CPA-deterioration rows, or the one sentence that is true instead.
 *
 * Three states, and the difference between the last two is the whole point: an
 * unavailable marker means we could not tell, an empty list means we looked and
 * nothing cleared the gate. Rendering both as an empty frame would turn "the
 * pull is too short" into "nothing is getting worse".
 */
/**
 * One Top movers list's rows.
 *
 * Mirrors CpaRows, including the part that matters most: an unavailable marker
 * renders its reason, never an empty list. "No campaign moved" is a claim, and
 * it must not be made on behalf of a comparison that could not be made.
 */
function MoverRows({ list, direction, onCampaign }) {
  if (isUnavailable(list)) {
    return (
      <div className="lempty">
        {list.reason === 'loading' ? 'Reading the last pull…' : list.needs}
      </div>
    );
  }
  if (!list.length) {
    return (
      <div className="lempty">
        {`No campaign converted at least ${MOVERS_MIN_DELTA} more`}
        {direction === 'up' ? '' : ' or fewer'}
        {' than it did in the period before.'}
      </div>
    );
  }
  return list.map((m) => (
    <div className="lr" key={m.id}>
      <span className="nm">
        <RankedCampaign id={m.id} name={m.name} metric="conversions" onCampaign={onCampaign} />
        <CampaignPeriod period={m.period} />
        <em>
          {m.isNew
            ? 'started — nothing in the period before'
            : `${fmt(m.previous, 'int')} → ${fmt(m.current, 'int')} conversions`}
        </em>
      </span>
      <span className={`vv ${direction === 'up' ? 'up' : 'down'}`}>
        {signed(m.delta)}
        <em className="neu">{m.pct === null ? 'no prior base' : `${signed(m.pct * 100)}%`}</em>
      </span>
    </div>
  ));
}

function CpaRows({ list, onCampaign }) {
  if (isUnavailable(list)) {
    return (
      <div className="lempty">
        {list.reason === 'loading' ? 'Reading the last pull…' : list.needs}
      </div>
    );
  }
  if (!list.length) {
    return (
      <div className="lempty">
        No campaign’s cost per conversion rose by
        {` ${Math.round(CPA_MIN_WORSENING * 100)}% `}
        or more between the two periods.
      </div>
    );
  }
  return list.map((c) => (
    <div className="lr" key={c.id}>
      <span className="nm">
        <RankedCampaign id={c.id} name={c.name} metric="cpa" onCampaign={onCampaign} />
        <CampaignPeriod period={c.period} />
        <em>
          {`${money(c.was, c.currency, 2)} → ${money(c.now, c.currency, 2)}`}
          {` · ${fmt(c.conversions, 'int')} conv.`}
        </em>
      </span>
      <span className="vv down">
        {`+${fmt(c.change, 'pct')}`}
        <em>{`${money(c.excess, c.currency, 0)} extra`}</em>
      </span>
    </div>
  ));
}

function CampaignPeriod({ period }) {
  return period ? <em>{period.daysPerPeriod} days per period · {period.priorRange.start}–{period.priorRange.end} → {period.currentRange.start}–{period.currentRange.end}</em> : null;
}

function ComparisonBasis({ deltas }) {
  return deltas?.independentPeriods ? <>Each campaign uses its own two equal periods within the selected dates, excluding its latest reported day. {deltas.comparedCampaigns} of {deltas.totalCampaigns} campaigns have enough daily history. Period lengths can differ; see each row. </> : <>The newest <b>{deltas?.daysPerPeriod || 0} days</b> against the {deltas?.daysPerPeriod || 0} before them, from the last pull’s daily rows. </>;
}

function RankedCampaign({ id, name, metric = 'spend', onCampaign }) {
  return onCampaign ? <button type="button" className="ow-ranked-campaign" onClick={() => onCampaign(id, metric)}>{name}</button> : name;
}

function PortfolioOverviewLists({ onCampaign, onViewAll, spenders, notConverting, wasteTotal, cur, cpaWorse, deltas, performanceV2, moversUp, moversDown, capped }) {
  return (
      <div className="g3">
        <ListFrame
          icon="payments"
          color="#0b2b3b"
          title="Top spenders" onViewAll={onViewAll ? () => onViewAll('spend') : undefined}
          meta="cost · CPA"
          foot={<span className="noact">No action — this list is for context.</span>}
        >
          {spenders.length ? spenders.map((r) => (
            <div className="lr" key={r.id}>
              <span className="nm">
                <RankedCampaign id={r.id} name={r.name} onCampaign={onCampaign} />
                {r.type ? <em>{r.type}</em> : null}
              </span>
              <span className="vv">
                {money(r.cost, r.currency, 0)}
                <em className="neu">{r.cpa === null ? 'no CPA' : `CPA ${money(r.cpa, r.currency, 2)}`}</em>
              </span>
            </div>
          )) : <div className="lempty">No campaign has measured spend yet.</div>}
        </ListFrame>

        <ListFrame
          icon="delete_sweep"
          color="#c0474d"
          title="Spending, not converting" onViewAll={onViewAll ? () => onViewAll('zero') : undefined}
          meta={wasteTotal ? money(wasteTotal, cur, 0) : null}
          foot={(
            <>
              Gated at
              {' '}
              <b>spend &gt; 0 with zero conversions</b>
              .
              <span className="noact">
                {' '}
                Not gated on the conversion-lag window — that needs a daily
                series, so a young campaign can appear here.
              </span>
            </>
          )}
        >
          {notConverting.length ? notConverting.map((r) => (
            <div className="lr" key={r.id}>
              <span className="nm">
                <RankedCampaign id={r.id} name={r.name} onCampaign={onCampaign} />
                <em>{r.conversions === null ? 'conversions not measured' : '0 conversions'}</em>
              </span>
              <span className="vv down">{money(r.cost, r.currency, 0)}</span>
            </div>
          )) : <div className="lempty">No campaigns with measured zero conversions and spend in this selection.</div>}
        </ListFrame>

        <ListFrame
          icon="south_east"
          color="#854d0e"
          title="CPA deterioration" onViewAll={onViewAll ? () => onViewAll('cpa') : undefined}
          meta="was → now"
          foot={isUnavailable(cpaWorse) ? (
            <span className="noact">
              Needs two comparable periods of daily rows.
            </span>
          ) : (
            <>
              <ComparisonBasis deltas={deltas} />{'Gated at '}
              <b>{`≥${CPA_MIN_CONVERSIONS} conversions in BOTH periods`}</b>
              {` and a ≥${Math.round(CPA_MIN_WORSENING * 100)}% rise.`}
              <span className="noact">
                {' Ranked by the extra spend, not the percentage. '}
                {performanceV2 ? 'Uses campaigns with comparable saved daily reporting.' : 'Google Ads only — no other platform’s pull emits per-day rows.'}
              </span>
            </>
          )}
        >
          <CpaRows list={cpaWorse} onCampaign={onCampaign} />
        </ListFrame>

        {[
          { key: 'up', icon: 'trending_up', color: '#3b9a6d', title: 'Top movers — up', list: moversUp },
          { key: 'down', icon: 'trending_down', color: '#c0474d', title: 'Top movers — down', list: moversDown },
        ].map((l) => (
          <ListFrame
            key={l.key}
            icon={l.icon}
            color={l.color}
            title={l.title} onViewAll={onViewAll ? () => onViewAll(l.key) : undefined}
            meta="Δ conv."
            foot={isUnavailable(l.list) ? (
              <span className="noact">
                Needs two comparable periods of daily rows.
              </span>
            ) : (
              <>
                <ComparisonBasis deltas={deltas} />{'Gated at '}
                <b>{`≥${MOVERS_MIN_DELTA} conversion of movement`}</b>
                .
                <span className="noact">
                  {' Ranked by the size of the change, not the percentage — a campaign '}
                  {'moving 400 → 500 outweighs one moving 2 → 4. '}
                  {performanceV2 ? 'Uses campaigns with comparable saved daily reporting.' : 'Google Ads only — no other platform’s pull emits per-day rows.'}
                </span>
              </>
            )}
          >
            <MoverRows list={l.list} direction={l.key} onCampaign={onCampaign} />
          </ListFrame>
        ))}

        {capped ? <ListFrame icon="lock_open" color="#00785e" title="Capped and converting" onViewAll={onViewAll ? () => onViewAll('capped') : undefined} meta="current budget constraint"
          foot={<>Observed Google budget constraints with positive conversions in the selected dates. Delivery evidence covers {capped.covered} of {capped.total} selected campaigns. This does not estimate incremental headroom.</>}>
          {capped.rows.length ? capped.rows.slice(0, 3).map(row => <div className="lr" key={row.campaignId}>
            <span className="nm"><RankedCampaign id={row.id} name={row.campaignName} onCampaign={onCampaign} /><em>Budget constrained · {row.metrics.conversions} conversions</em></span>
            <span className="vv">{money(row.metrics.spend, row.currency, 0)}</span>
          </div>) : <div className="lempty">{capped.covered === capped.total && capped.total > 0 ? 'No observed budget-constrained campaigns with measured conversions.' : 'No matches in available evidence. Delivery evidence is incomplete for this selection.'}</div>}
        </ListFrame> : UNWIRED_LISTS.map((l) => (
          <ListFrame key={l.key} icon={l.icon} color={l.color} title={l.title} meta={l.meta}>
            <div style={{ padding: '11px 13px' }}>
              <NotWired>
                Needs
                {' '}
                {l.needs}
                .
              </NotWired>
            </div>
          </ListFrame>
        ))}
      </div>
  );
}

export default function PortfolioTab({
  accountId, userId, campaign, cards = [], runsByCampaign = {}, performanceV2 = false,
  // PerformancePage's super-admin ⤓ JSON export: called with usePortfolio's
  // `raw` (the responses this view is drawn from) whenever one lands.
  onRawData = null,
  campaignIds,
  onViewRecommendations,
  savedReport,
  overviewOnly = false,
  onOverviewCampaign, overviewScope,
}) {
  const [overviewList, setOverviewList] = useState(null);
  useEffect(() => { setOverviewList(null); }, [accountId, savedReport, overviewScope]);
  const [selectedCampaign, setSelectedCampaign] = useState(campaign?.id || 'all');
  const [metric, setMetric] = useState('cost');
  const [group, setGroup] = useState('performance');
  // Off by default: a pull covers one window, so comparing spends half the days
  // on the dashed line. Showing the whole window is the more useful default and
  // the comparison is one click away.
  const [compare, setCompare] = useState(false);

  // The date range. Remembered per account under its OWN scope: the grid's
  // picker spends money on every change and this one does not, so the two must
  // not share a stored window.
  const [timeFilter, setTimeFilter] = useState(() => loadStoredTimeFilter(accountId, 'accountRead'));
  useEffect(() => { setTimeFilter(loadStoredTimeFilter(accountId, 'accountRead')); }, [accountId]);
  useEffect(() => { storeTimeFilter(accountId, timeFilter, 'accountRead'); }, [accountId, timeFilter]);
  const dateWindow = useMemo(() => savedReport
    ? { startDate: savedReport.range.start, endDate: savedReport.range.end }
    : resolveTimeWindow(timeFilter), [timeFilter, savedReport]);

  // An empty workspace selection means no campaigns, never the whole account.
  const savedDataset = useMemo(() => savedReport?.performanceDataset ? {
    ...savedReport.performanceDataset,
    campaigns: savedReport.performanceDataset.campaigns.filter(c => savedReport.rows.some(row => row.campaignId === c.id)).map(c => ({
      ...c, performanceV2: c.performanceV2 ? { ...c.performanceV2,
        daily: (c.performanceV2.daily || []).filter(day => day.date >= savedReport.range.start && day.date <= savedReport.range.end),
      } : c.performanceV2,
    })),
  } : undefined, [savedReport]);

  const {
    loading, error, campaigns: gridCampaigns,
    series: seriesData, seriesLoading, seriesError,
    deltas: portfolioDeltas, deltasLoading, deltasError,
    windowed, windowedLoading, windowedError,
    raw, artifactsUnreadable,
  } = usePortfolio(accountId, userId, performanceV2 && selectedCampaign !== 'all' ? selectedCampaign : null, compare, dateWindow, performanceV2, campaignIds, savedDataset);

  // `raw` is memoized on the data, so this fires per response, not per render.
  useEffect(() => { if (onRawData) onRawData(raw); }, [onRawData, raw]);

  /**
   * The window the GRID's cached numbers cover — what the page shows when no
   * range is picked. The design's "Last 30 days vs previous 30 days" is never
   * printed over these: they are whatever the last pull asked for (lifetime, per
   * the grid's own `metricsWindowed: false`), and that copy over lifetime totals
   * would be the single most misleading thing on the page.
   */
  const scopedGridCampaigns = useMemo(() => campaignIds !== undefined
    ? gridCampaigns.filter(c => !campaignIds.length || campaignIds.includes(c.id))
    : performanceV2 && selectedCampaign !== 'all' ? gridCampaigns.filter((c) => c.id === selectedCampaign) : gridCampaigns,
  [gridCampaigns, performanceV2, selectedCampaign, campaignIds]);
  const scopedCards = useMemo(() => campaignIds !== undefined
    ? cards.filter(c => !campaignIds.length || campaignIds.includes(c.camp))
    : performanceV2 && selectedCampaign !== 'all' ? cards.filter((c) => c.camp === selectedCampaign) : cards,
  [cards, performanceV2, selectedCampaign, campaignIds]);
  const win = useMemo(() => metricsWindow(scopedGridCampaigns), [scopedGridCampaigns]);

  /**
   * Every number below this line is about the picked range.
   *
   * The swap happens ONCE, here, rather than in each derivation: totals, tiles,
   * the table, the lists and the pacing spend all read these rows, so a range
   * applied to some of them and not others cannot happen by omission.
   */
  const applied = useMemo(
    () => applyWindow(scopedGridCampaigns, dateWindow ? windowed : null),
    [scopedGridCampaigns, dateWindow, windowed],
  );
  const campaigns = applied.campaigns;
  const deltas = useMemo(() => overviewOnly ? campaignPeriodDeltas(campaigns) : portfolioDeltas,
    [overviewOnly, campaigns, portfolioDeltas]);

  /**
   * A range is picked but its roll-up has not landed (or failed). The numbers on
   * screen are still the grid's un-windowed ones, so the page says so instead of
   * showing them under the range's label — a lifetime total wearing a "last 7
   * days" heading is the exact misread this whole page is built to avoid.
   */
  const windowPending = Boolean(dateWindow) && (windowedLoading || !windowed);
  const rangeLabel = dateWindow ? describeTimeWindow(dateWindow) : win.label;

  const t = useMemo(() => {
    if (!performanceV2) return totals(campaigns);
    const { currency, mixed } = reportingCurrency(campaigns);
    return { ...metricCoverageTotals(campaigns, mixed).values, currency, mixedCurrency: mixed };
  }, [campaigns, performanceV2]);
  // The third argument is this branch's: campaignRows reads each campaign's own
  // runs for the Competitive and Quality cells. `win` is NOT redefined here —
  // main moved it above applyWindow, where it describes the GRID's cached
  // window rather than the windowed rows, which is the distinction the date
  // picker introduced.
  const rows = useMemo(
    () => campaignRows(campaigns, scopedCards, runsByCampaign).map((row) => {
      if (!performanceV2) return row;
      const metrics = campaigns.find((c) => c.id === row.id)?.performanceV2?.metrics || {};
      return { ...row, ...Object.fromEntries(V2_CONVERSION_KEYS.filter((key) => !['conversions', 'cpa'].includes(key)).map((key) => [key, metrics[key] ?? null])) };
    }),
    [campaigns, scopedCards, runsByCampaign, performanceV2],
  );
  const tiles = useMemo(() => scorecard(t), [t]);
  const hl = useMemo(() => health(scopedCards, rows), [scopedCards, rows]);
  const spenders = useMemo(() => topSpenders(rows), [rows]);

  /**
   * Spending without converting. Derivable from what we store, so it is real —
   * but gated the way the design gates every list ("a volume threshold so noise
   * cannot rank"): a campaign that has spent almost nothing and converted
   * nothing is not waste, it is a campaign that has barely run.
   *
   * There is no conversion-lag gate here, and that is a genuine weakness rather
   * than an oversight: the lag window needs a daily series to know how much of
   * this spend is still inside it. The footer says so, because a reader who
   * assumes the gate is present would treat a young campaign as waste.
   */
  const notConverting = useMemo(() => rows
    .filter((r) => r.cost !== null && r.cost > 0 && (r.conversions === 0 || (!overviewOnly && r.conversions === null)))
    .sort((a, b) => b.cost - a.cost)
    .slice(0, 3), [rows, overviewOnly]);

  const wasteTotal = useMemo(
    () => notConverting.reduce((a, r) => a + (r.cost || 0), 0),
    [notConverting],
  );

  /**
   * CPA deterioration, against the older half of the pull's own days.
   *
   * Loading and failure become markers rather than an empty list, for the same
   * reason the chart does it: an empty ranked list says "nothing is getting
   * worse", which is a claim, and it must not be made on behalf of a request
   * that has not answered yet.
   */
  const cpaWorse = useMemo(() => {
    if (deltasLoading) return unavailable('loading', 'the last pull’s artifact is still being read');
    if (deltasError) return unavailable('the comparison request failed', deltasError);
    return cpaDeterioration(deltas, rows);
  }, [deltas, deltasLoading, deltasError, rows]);

  /**
   * Top movers, off the SAME comparison — one request, three lists, one
   * definition of which day "before" ends on.
   */
  const movers = useMemo(() => {
    if (deltasLoading) return { up: unavailable('loading', 'the last pull’s artifact is still being read'), down: null };
    if (deltasError) return { up: unavailable('the comparison request failed', deltasError), down: null };
    return {
      up: topMovers(deltas, rows, { direction: 'up' }),
      down: topMovers(deltas, rows, { direction: 'down' }),
    };
  }, [deltas, deltasLoading, deltasError, rows]);
  // Both directions share one marker when there is no comparison at all: the
  // reason is a property of the request, not of a direction.
  const moversUp = movers.up;
  const moversDown = movers.down === null ? movers.up : movers.down;

  /**
   * The trend chart's two lines, for whichever metric the chips have selected.
   *
   * The server returns a full metric object per day for BOTH periods, so
   * switching metric is a re-map rather than a refetch — the artifact behind it
   * is multi-MB and re-reading it to change the y-axis would be absurd.
   *
   * Anything other than a usable series becomes an `unavailable` marker rather
   * than an empty array, so the chart can say WHICH of the several reasons
   * applies: still loading, request failed, or the pull genuinely had too few
   * days. The server sends its own `reason` for the last case; it is quoted
   * rather than re-derived here.
   */
  const chartSeries = useMemo(() => {
    if (seriesLoading) return unavailable('loading', 'the last pull’s artifact is still being read');
    if (seriesError) return unavailable('the series request failed', seriesError);
    const rows = seriesData?.series || [];
    if (!rows.length) {
      return unavailable(
        'not enough daily data',
        seriesData?.reason || NEEDS.series,
      );
    }
    return rows.map((p) => ({
      date: p.date,
      current: p.current?.[metric] ?? null,
      // null in full-window mode — the chart reads that as "draw one line".
      previous: p.previous?.[metric] ?? null,
    }));
  }, [seriesData, seriesLoading, seriesError, metric]);

  if (loading) return <div className="perf-loading">Loading the summary…</div>;
  if (error) return <div className="perf-error">{error}</div>;

  const columnGroups = performanceV2 ? V2_COLUMN_GROUPS : COLUMN_GROUPS;
  const activeGroup = columnGroups.find((g) => g.key === group) || columnGroups[0];
  const cur = t.currency;
  // On a mixed-currency account the money metrics are withheld rather than
  // plotted (see CURRENCY_FREE_TREND_METRICS). `trendMetric` keeps the chart on
  // a metric that is still offered — a remembered 'cost' selection would
  // otherwise leave it rendering nothing with no way back.
  const trendMetrics = t.mixedCurrency ? CURRENCY_FREE_TREND_METRICS : TREND_METRICS;
  const trendMetric = trendMetrics.some((m) => m.key === metric) ? metric : trendMetrics[0].key;
  const platforms = [...new Set(rows.map((r) => r.type).filter(Boolean))];

  const delivery = savedReport ? googlePortfolioContext(savedReport.rows, savedReport.children || []) : null;
  const capped = delivery ? { covered: delivery.covered, total: savedReport.rows.length,
    rows: savedReport.rows.filter(row => delivery.budget.includes(row.id) && Number.isFinite(row.metrics?.conversions) && row.metrics.conversions > 0),
  } : null;
  const overviewProps = { onCampaign: overviewOnly ? onOverviewCampaign : undefined, onViewAll: overviewOnly && onOverviewCampaign ? setOverviewList : undefined, capped, spenders, notConverting, wasteTotal, cur, cpaWorse, deltas, performanceV2, moversUp, moversDown };
  if (overviewOnly) return <section className="pf ow-performance-overview" aria-label="Campaign performance overview">
    <p className="pmeta">{campaigns.length} campaigns · {rangeLabel}. Movement compares each campaign’s own saved daily periods; adding campaigns does not change those periods.</p>
    <PortfolioOverviewLists {...overviewProps} />
    {overviewList && onOverviewCampaign && <PortfolioOverviewDrilldown key={overviewList} listKey={overviewList}
      items={overviewMatches(overviewList, { ...overviewProps, rows })} campaigns={campaigns} rows={rows} rangeLabel={rangeLabel}
      onCampaign={onOverviewCampaign} onClose={() => setOverviewList(null)} />}
  </section>;

  return (
    <div className={`pf${performanceV2 ? ' perf-v2-page' : ''}`}>
      <div className="phead">
        <div><h1>{performanceV2 ? (selectedCampaign !== 'all' ? 'Campaign summary' : 'Summary') : campaign?.name || 'Summary'}</h1></div>
        {performanceV2 && campaignIds === undefined && <CampaignFilter value={selectedCampaign} onChange={setSelectedCampaign} allCount={gridCampaigns.length}
          options={gridCampaigns.map((c) => ({ key: c.id, label: c.name, count: c.performanceV2?.groups?.length || 0 }))} />}
        <span className="pmeta">
          {campaigns.length}
          {campaigns.length === 1 ? ' campaign' : ' campaigns'}
          {platforms.length ? (performanceV2 ? ` · ${platforms.length} ${platforms.length === 1 ? 'platform' : 'platforms'}` : ` · ${platforms.join(', ')}`) : ''}
        </span>
        <div className="gap" />

        {/* THE DATE RANGE. Same preset vocabulary as the Campaign Grid's picker,
            deliberately different mechanics: a pick there blanks the grid and
            dispatches a paid reporting-API pull that overwrites the shared
            campaign_grid_metrics row. Here it re-derives from the per-day rows
            already in the last pull's artifact — free, instant, and it touches
            no shared state. That is also why the custom range needs no Fetch
            button: nothing is being bought, so it can apply as you type. */}
        {!savedReport && <label className="pwin">
          <span className="pwin-l">Range</span>
          <select
            className="pwin-s"
            aria-label="Date range"
            value={timeFilter.preset}
            onChange={(e) => setTimeFilter((f) => (
              e.target.value === 'custom'
                ? { ...f, preset: 'custom' }
                : { preset: e.target.value, startDate: '', endDate: '' }
            ))}
          >
            {GRID_TIME_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          {timeFilter.preset === 'custom' ? (
            <>
              <input
                type="date"
                className="pwin-d"
                aria-label="Range start"
                value={timeFilter.startDate}
                onChange={(e) => setTimeFilter((f) => ({ ...f, startDate: e.target.value }))}
              />
              <span className="pwin-l">to</span>
              <input
                type="date"
                className="pwin-d"
                aria-label="Range end"
                value={timeFilter.endDate}
                onChange={(e) => setTimeFilter((f) => ({ ...f, endDate: e.target.value }))}
              />
            </>
          ) : null}
        </label>}
      </div>

      {/* What the numbers below are, and what they are not. One line, always
          present, because every state here is one a reader can be wrong about:
          which range, how much of the account it covers, and whether the pull
          reaches back far enough to answer at all. */}
      <div className={`pwmeta${windowPending || windowedError ? ' pend' : ''}`}>
        <span aria-hidden="true" className="material-icons">schedule</span>
        {dateWindow ? (
          <>
            <b>{rangeLabel}</b>
            {windowedError ? (
              <em>
                {'the roll-up for this range failed to load — the numbers below are '}
                {'still the last pull\'s totals'}
              </em>
            ) : windowPending ? (
              <em>reading the range — the numbers below are still the last pull’s totals</em>
            ) : (
              <>
                <em>
                  {`${applied.covered} of ${campaigns.length} ${campaigns.length === 1 ? 'campaign' : 'campaigns'}`}
                  {applied.uncovered.length ? performanceV2 ? ` with daily data · ${applied.uncovered.length} without data in this range` : ' — the rest report no per-day data (Google Ads and DV360 pulls emit it; LinkedIn and Meta do not, and a DV360 pull predating per-day rows needs a re-pull), so they show “—” rather than a lifetime total' : ''}
                </em>
                {/* Nothing at all resolved. The server knows WHY — the range
                    predates the pull, or no pull emitted per-day rows — and its
                    reason is quoted rather than re-derived, because "nothing
                    delivered" and "we never pulled this" look identical here
                    and are completely different things to tell a reader. */}
                {applied.covered === 0 && windowed?.reason ? (
                  <em>{windowed.reason}</em>
                ) : null}
                {windowed?.coverage ? (
                  <em className="cov">
                    {`pull covers ${windowed.coverage.startDate} → ${windowed.coverage.endDate}`}
                  </em>
                ) : null}
              </>
            )}
          </>
        ) : (
          <>
            <b>{win.label}</b>
            <em>the last pull’s totals — pick a range to read part of it</em>
          </>
        )}
      </div>

      <div className="cards">
        {tiles.map((tile) => <Tile key={tile.label} tile={tile} currency={cur} />)}
      </div>

      <div className={`health${hl.clear ? ' clear' : ''}`}>
        {hl.clear ? (
          <span className="hi good">
            <span aria-hidden="true" className="material-icons">check_circle</span>
            Nothing outstanding — no open findings and no flight ending soon.
          </span>
        ) : (
          <>
            {hl.diagnostics ? (
              <span className="hi bad">
                <span aria-hidden="true" className="material-icons">error</span>
                <span className="n">{hl.diagnostics}</span>
                {hl.diagnostics === 1 ? ' diagnostic finding' : ' diagnostic findings'}
              </span>
            ) : null}
            {hl.pacing ? (
              <span className="hi warn">
                <span aria-hidden="true" className="material-icons">warning</span>
                <span className="n">{hl.pacing}</span>
                {hl.pacing === 1 ? ' pacing finding' : ' pacing findings'}
              </span>
            ) : null}
            {hl.ending ? (
              <span className="hi warn">
                <span aria-hidden="true" className="material-icons">schedule</span>
                <span className="n">{hl.ending}</span>
                {hl.ending === 1 ? ' flight ends' : ' flights end'}
                {hl.endingSoonest ? ` within ${hl.endingSoonest.days}d` : ''}
              </span>
            ) : null}
            <span className="info">
              {performanceV2 ? 'Open findings for this selection' : '· counted from completed runs, filtered by the same dismissals as the tabs'}
            </span>
          </>
        )}
        {onViewRecommendations && <button type="button" className="perf-v2-recommendations" onClick={onViewRecommendations}>View recommendations <span aria-hidden="true" className="material-icons">arrow_forward</span></button>}
        {!onViewRecommendations && performanceV2 && selectedCampaign !== 'all' && <a className="perf-v2-recommendations" href={`/performance/${encodeURIComponent(selectedCampaign)}?tab=recommendations`}>
          View recommendations <span aria-hidden="true" className="material-icons">arrow_forward</span>
        </a>}
      </div>

      {/* Full width. The pacing card used to sit beside this at 1fr; with it
          gone the chart takes the whole row rather than leaving a third of the
          page empty, and the plot is taller to match. */}
      {performanceV2 && <PerformanceV2Summary campaigns={campaigns} rangeLabel={rangeLabel} failures={artifactsUnreadable} />}
      <div className="row1">
        <TrendChart
          metrics={trendMetrics}
          metric={trendMetric}
          totals={performanceV2 && seriesData?.series?.length ? {
            current: seriesData.totals?.current?.[trendMetric] ?? null,
            previous: seriesData.totals?.previous?.[trendMetric] ?? null,
          } : null}
          onMetric={setMetric}
          series={chartSeries}
          currency={cur}
          windowLabel={rangeLabel}
          coverage={seriesData?.campaignsCovered ?? null}
          // So a partly-covered chart can say whether the missing campaigns are
          // on a platform that reports no days, or simply have not been
          // re-pulled since per-day rows started being emitted.
          newestPullAt={seriesData?.newestPullAt ?? null}
          totalCampaigns={campaigns.length}
          daysPerPeriod={seriesData?.daysPerPeriod ?? null}
          compared={Boolean(seriesData?.compared)}
          onToggleCompare={() => setCompare((c) => !c)}
          compareRequested={compare}
          latestDayExcluded={seriesData?.latestDayExcluded ?? null}
        />
      </div>

      <PortfolioOverviewLists {...overviewProps} />

      <div className="ctabs">
        {columnGroups.map((g) => (
          <button
            key={g.key}
            type="button"
            className={`tb${g.key === group ? ' on' : ''}`}
            onClick={() => setGroup(g.key)}
          >
            {g.label}
          </button>
        ))}
      </div>
      <div className="twrap">
        {activeGroup.needs ? (
          <div style={{ padding: '14px 0' }}>
            <NotWired>
              The
              {' '}
              {activeGroup.label}
              {' '}
              group needs
              {' '}
              {activeGroup.needs}
              .
            </NotWired>
          </div>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Campaign</th>
                  {activeGroup.cols.map((c) => (
                    <th key={c.key} className={c.num ? 'num' : ''}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  // `windowed === false` means a range is picked and this
                  // campaign has no per-day rows to compute it from. Its cells
                  // are dashes, not zeros, and the row is dimmed and titled so
                  // the dashes cannot be read as "this campaign spent nothing".
                  <tr key={r.id} className={r.windowed === false ? 'nowin' : ''}>
                    <td title={r.windowed === false ? 'No per-day data for this campaign — Google Ads and DV360 pulls emit it; LinkedIn and Meta do not, and a DV360 pull from before per-day rows were added needs a re-pull. This range cannot be computed for it.' : undefined}>
                      {r.flags.map((f) => (
                        <span key={f.kind} className={`fl ${f.kind}`}>{f.label}</span>
                      ))}
                      {performanceV2 ? <button type="button" className="perf-v2-campaign-link" onClick={() => setSelectedCampaign(r.id)}>{r.name}</button> : r.name}
                      {r.windowed === false ? <em className="tnone"> · not in range data</em> : null}
                    </td>
                    {activeGroup.cols.map((c) => {
                      const v = r[c.key];
                      if (c.key === 'flightEnd') {
                        const d = daysToFlightEnd(v);
                        return (
                          <td key={c.key}>
                            {v ? String(v).slice(0, 10) : <span className="tnone">no end date</span>}
                            {d !== null && d >= 0 && d <= 7 ? ` · ${d}d` : ''}
                          </td>
                        );
                      }
                      if (c.render === 'competitive') {
                        return <td key={c.key}><CompetitiveCell read={v} /></td>;
                      }
                      if (c.render === 'quality') {
                        return <td key={c.key}><QualityCell q={v} /></td>;
                      }
                      if (!c.num) {
                        return <td key={c.key}>{v || <span className="tnone">—</span>}</td>;
                      }
                      return (
                        <td key={c.key} className="num">
                          {v === null || v === undefined
                            ? <span className="tnone">—</span>
                            : fmt(v, c.format, r.currency)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {!rows.length ? (
                  <tr>
                    <td colSpan={activeGroup.cols.length + 1}>
                      No campaigns have been pulled for this account yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
            <div className="cap">
              Column groups mirror Google&apos;s own taxonomy, because that is what
              people are trained on.
              {' '}
              {/* The no-delta caveat is about the METRICS PULL, so it is printed
                  only over the groups that read from it. Competitive and Quality
                  come out of optimizer runs, which do carry a completion time —
                  saying they have no date dimension would be false about them. */}
              {activeGroup.fromRuns ? (
                <>
                  This group is read from each campaign&apos;s most recent optimizer run,
                  not from the metrics pull, so a cell is
                  {' '}
                  <b>as current as that run and no more</b>
                  ; hover one for its evidence and the date it was taken.
                </>
              ) : performanceV2 ? (
                <>Metrics use saved reporting for {rangeLabel}. Campaigns without observations remain unavailable.</>
              ) : (
                <>
                  Every figure here is a lifetime or last-pull total —
                  {' '}
                  <b>none of them carries a delta</b>
                  , because the cached metrics have no date dimension to compare
                  across.
                </>
              )}
              {activeGroup.note ? ` ${activeGroup.note}` : ''}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
